/**
 * useFirestore.js — the only place the app subscribes to Firestore.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *
 *  1. ~30 LEAKED LISTENERS. The dominant idiom in v1 was
 *         return () => unsubscribe;
 *     a cleanup function that evaluates the unsubscribe reference and throws
 *     it away without calling it. It was also usually returned from a plain
 *     helper function that useEffect never received, so nothing was torn down
 *     for the lifetime of the app. AuthContext opened three new settings
 *     listeners on every write to the business document.
 *
 *  2. NO ERROR PATH. Every onSnapshot was registered with a success callback
 *     only, or with an error handler that console.log'd. A permission-denied
 *     or missing-index failure was indistinguishable from an empty collection,
 *     so the UI showed "no data" for what was actually a broken query.
 *
 *  3. UNBOUNDED READS. `limit()` and `startAfter()` appeared zero times in the
 *     v1 source. Every list screen subscribed to an entire, unbounded
 *     collection — including the invoice history, which grows forever.
 *
 *  4. SETSTATE AFTER UNMOUNT, on every one of those leaked listeners.
 *
 * These hooks always unsubscribe, always surface errors, and always take a
 * bounded query.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  onSnapshot,
  getDocs,
  query as buildQuery,
  limit as limitTo,
  startAfter,
} from 'firebase/firestore';

/** Normalise a Firestore error into something a user can act on. */
export function describeFirestoreError(error) {
  if (!error) return null;
  switch (error.code) {
    case 'permission-denied':
      return 'You do not have permission to view this. If you just signed in, try reloading.';
    case 'failed-precondition':
      return 'This view needs a database index that has not been created yet.';
    case 'unavailable':
      return 'Cannot reach the server. Check your connection — changes will sync when you are back online.';
    case 'resource-exhausted':
      return 'The database quota has been exceeded. Please try again later.';
    case 'unauthenticated':
      return 'Your session has expired. Please sign in again.';
    default:
      return error.message || 'Something went wrong loading this data.';
  }
}

/**
 * Subscribe to a query.
 *
 * @param {Query|null} query  a Firestore Query, or null to stay idle
 * @param {Object} options
 * @param {Array} options.deps  extra dependencies that should re-subscribe
 * @returns {{ data, loading, error, empty }}
 */
export function useCollection(query, { deps = [], transform = null } = {}) {
  const [state, setState] = useState({ data: [], loading: !!query, error: null });
  const transformRef = useRef(transform);
  transformRef.current = transform;

  useEffect(() => {
    if (!query) {
      setState({ data: [], loading: false, error: null });
      return undefined;
    }

    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));

    const unsubscribe = onSnapshot(
      query,
      (snapshot) => {
        if (!active) return;
        const rows = snapshot.docs.map((d) => {
          const row = { id: d.id, ...d.data() };
          return transformRef.current ? transformRef.current(row, d) : row;
        });
        setState({ data: rows, loading: false, error: null });
      },
      (error) => {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.error('[firestore] subscription failed', error);
        setState({ data: [], loading: false, error });
      }
    );

    // The cleanup CALLS unsubscribe. This one character is the v1 leak.
    return () => {
      active = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    ...state,
    empty: !state.loading && !state.error && state.data.length === 0,
    errorMessage: describeFirestoreError(state.error),
  };
}

/** Subscribe to a single document. */
export function useDocument(ref, { deps = [] } = {}) {
  const [state, setState] = useState({ data: null, exists: false, loading: !!ref, error: null });

  useEffect(() => {
    if (!ref) {
      setState({ data: null, exists: false, loading: false, error: null });
      return undefined;
    }

    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!active) return;
        // exists is a METHOD on the modular SDK. v1 read it as a property,
        // which is always truthy, so a missing document was treated as present
        // and spread into state as `{ id, ...undefined }`.
        const exists = snapshot.exists();
        setState({
          data: exists ? { id: snapshot.id, ...snapshot.data() } : null,
          exists,
          loading: false,
          error: null,
        });
      },
      (error) => {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.error('[firestore] document subscription failed', error);
        setState({ data: null, exists: false, loading: false, error });
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, errorMessage: describeFirestoreError(state.error) };
}

/**
 * Cursor-paginated collection reads.
 *
 * Lists in v1 subscribed to whole collections. This fetches a page at a time
 * with a real cursor, so a business with 40,000 invoices loads the same amount
 * of data as one with 40.
 */
export function usePagedCollection(baseQuery, { pageSize = 25, deps = [] } = {}) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const cursorRef = useRef(null);
  const activeRef = useRef(true);
  /**
   * The query is read through a ref, and the lifecycle effect depends on
   * `deps` only — never on the query object itself.
   *
   * Callers build their query inside a useMemo, but a caller that forgets and
   * passes an inline object hands us a new identity on every render. If that
   * identity were an effect dependency, the effect would reset state, trigger
   * a render, and loop forever. Depending on `deps` keeps a mistake in one
   * screen from hanging the browser.
   */
  const baseQueryRef = useRef(baseQuery);
  baseQueryRef.current = baseQuery;
  /**
   * Re-entrancy guard. This MUST be a ref, not the `loading` state.
   *
   * The previous version guarded with `if (loading) return`, read from the
   * closure. React state updates are asynchronous and the closure is stale, so
   * two calls fired in the same tick both saw `loading === false` and both ran.
   * Under React 18 StrictMode, which mounts every component twice in
   * development, that is exactly what happened on first render: the first page
   * was fetched twice and appended to `pages` twice, so every row in every
   * list appeared duplicated — an invoice looked like it had saved twice when
   * only one document existed.
   *
   * A ref updates synchronously, so the second call sees the guard immediately.
   */
  const inFlightRef = useRef(false);
  /** Generation counter: a reset invalidates any page still in flight. */
  const generationRef = useRef(0);

  const reset = useCallback(() => {
    generationRef.current += 1;
    inFlightRef.current = false;
    cursorRef.current = null;
    setPages([]);
    setDone(false);
    setError(null);
    setLoading(false);
  }, []);

  const loadMore = useCallback(async () => {
    const activeQuery = baseQueryRef.current;
    if (!activeQuery) return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    const generation = generationRef.current;

    setLoading(true);
    setError(null);
    try {
      const q = cursorRef.current
        ? buildQuery(activeQuery, startAfter(cursorRef.current), limitTo(pageSize))
        : buildQuery(activeQuery, limitTo(pageSize));
      const snapshot = await getDocs(q);

      // Dropped if the component unmounted or the query changed mid-flight.
      if (!activeRef.current || generation !== generationRef.current) return;

      const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      cursorRef.current = snapshot.docs[snapshot.docs.length - 1] || cursorRef.current;
      setPages((prev) => [...prev, rows]);
      if (snapshot.docs.length < pageSize) setDone(true);
    } catch (e) {
      if (!activeRef.current || generation !== generationRef.current) return;
      // eslint-disable-next-line no-console
      console.error('[firestore] page load failed', e);
      setError(e);
    } finally {
      if (generation === generationRef.current) inFlightRef.current = false;
      if (activeRef.current && generation === generationRef.current) setLoading(false);
    }
  }, [pageSize]);

  /**
   * One effect owns the lifecycle: reset on a dependency change, fetch the
   * first page, and invalidate on unmount. The previous version split this
   * across three effects whose ordering let a `started` flag be cleared by a
   * later effect in the same commit, which is what allowed the double fetch.
   */
  useEffect(() => {
    activeRef.current = true;
    reset();
    if (baseQueryRef.current) loadMore();

    return () => {
      activeRef.current = false;
      // Invalidate anything in flight so a late response cannot append to the
      // next mount's page list.
      generationRef.current += 1;
      inFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  /**
   * Flatten and de-duplicate by document id.
   *
   * Belt and braces: even with the guard above, a cursor-paginated read can
   * legitimately return a document twice if a concurrent write reorders the
   * result set between pages. A billing list must never show the same invoice
   * twice, so the id is the last word.
   */
  const data = useMemo(() => {
    const seen = new Set();
    const rows = [];
    pages.forEach((page) => {
      page.forEach((row) => {
        if (seen.has(row.id)) return;
        seen.add(row.id);
        rows.push(row);
      });
    });
    return rows;
  }, [pages]);

  return {
    data,
    loading,
    error,
    errorMessage: describeFirestoreError(error),
    done,
    empty: !loading && !error && data.length === 0,
    loadMore,
    reset,
  };
}


/**
 * Wrap an async mutation with loading/error state and an unmount guard, so a
 * component never calls setState after it has gone away and a failure never
 * shows a success toast.
 */
export function useMutation(fn) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const inFlightRef = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args) => {
      // Synchronous re-entrancy guard. Callers disable their button on
      // `pending`, but that is state: between the click and the re-render a
      // second click still lands. For a mutation that reserves an invoice
      // number, running twice means burning a number, so the guard is a ref.
      if (inFlightRef.current) return { ok: false, error: new Error('Already saving.'), skipped: true };
      inFlightRef.current = true;

      setPending(true);
      setError(null);
      try {
        const result = await fn(...args);
        inFlightRef.current = false;
        if (mounted.current) setPending(false);
        return { ok: true, result };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[mutation] failed', e);
        inFlightRef.current = false;
        if (mounted.current) {
          setError(e);
          setPending(false);
        }
        return { ok: false, error: e };
      }
    },
    [fn]
  );

  return { run, pending, error, errorMessage: error?.message || null };
}

/** Debounce a rapidly changing value — used for search inputs. */
export function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
